/**
 * GET /customers/:id/snapshot and GET /customers/activity (CRM-API-4,
 * api#1058) — fieldiq-api `src/lib/customer-snapshot.ts`. Hand-written; shapes
 * read off a live local response at api 3db3ac9.
 *
 * EVERY FIGURE IS `{ value, asOf, reason? }`. `value: null` arrives WITH a
 * `reason` and renders "—" plus that reason — never 0. A real 0 is a 0. These
 * are the figures the future Scout agent will cite, so if one looks wrong it
 * is fixed in the API, never recomputed here.
 */
export interface Figure<T> {
  value: T | null;
  asOf: string;
  reason?: string;
}

export interface CustomerSnapshot {
  customerId: string;
  customerName: string;
  customerStatus: string | null;
  currency: string | null;
  asOf: string;
  windows: { timeZone: string; today: string; last90dFrom: string; last12mFrom: string };
  work: {
    lastJobDate: Figure<string>;
    jobsLast90d: Figure<number>;
    openJobs: Figure<number>;
    lastTicketWorkDate: Figure<string>;
    ticketsLast90d: Figure<number>;
  };
  money: {
    invoicedLast12m: Figure<number>;
    arOutstanding: Figure<number>;
    arOverdue: Figure<number>;
  };
  sales: {
    openQuotes: Figure<{
      count: number;
      latest: { id: string; quoteNumber: string | null; status: string; totalAmount: number | null; currency: string | null } | null;
    }>;
    openOpportunities: Figure<{ count: number; valueKnownTotal: number; valueUnknownCount: number }>;
    lastMeetingAt: Figure<string>;
    nextFollowUpDate: Figure<string>;
  };
  contacts: {
    count: Figure<number>;
    primary: Figure<{ id: string; name: string; email: string | null; phone: string | null; role: string | null }>;
  };
}

export interface CustomerActivityRow {
  customerId: string;
  customerName: string;
  status: string;
  ownerUserId: string | null;
  lastJobDate: string | null;
  lastTicketWorkDate: string | null;
  lastInvoiceDate: string | null;
  lastMeetingDate: string | null;
  /** null = no activity of any kind, ever. */
  lastActivityDate: string | null;
}

export interface CustomerActivity {
  asOf: string;
  windows: { timeZone: string; today: string; quietBefore: string | null };
  rows: CustomerActivityRow[];
}

/** Exhaustive over the API's reasons; an unknown reason says only that the figure is unavailable. */
export const SNAPSHOT_REASON_LABELS: Record<string, string> = {
  no_past_job: 'No past job',
  no_ticket: 'No ticket yet',
  no_confirmed_meeting: 'No meeting logged',
  no_open_follow_up: 'No open follow-up',
  no_primary_contact: 'No primary contact',
};

export function snapshotReasonLabel(reason: string | undefined): string {
  return (reason && SNAPSHOT_REASON_LABELS[reason]) || 'Not available';
}
